import React, { useState, useEffect, useCallback } from 'react';
import ForecastAccuracyGrid from './ForecastAccuracyGrid';
import ForecastDropdown from "./ForecastDropdown";
import ForecastPeriodDropdown from "./ForecastPeriodDropdown";
import Box from "@mui/material/Box";
import axiosInstance from "../AxiosInstance";
import IconButton from "@mui/material/IconButton";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { Skeleton, Paper, Grid, Tabs, Tab, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { downloadCsv } from "../Utilities/csvUtility";

interface ForecastPeriod {
    forecastPeriodId: number;
    periodIndex: number;
    periodName: string;
}

interface Forecast {
    forecastId: number;
    name: string;
    startDate: string;
    forecastPeriods: ForecastPeriod[];
}

interface ForecastAccuracyData {
    periodNames: string[];
    rows: Array<{
        id: string;
        customerName?: string;
        sku?: string;
        description?: string;
        periods: Array<{
            forecast: number | null;
            actual: number | null;
            variance: number | null;
            absoluteVariance: number | null;
        }>;
        children?: Array<{
            id: string;
            customerName: string;
            periods: Array<{
                forecast: number | null;
                actual: number | null;
                variance: number | null;
                absoluteVariance: number | null;
            }>;
        }>;
    }>;
}

type AggregationPeriod = 'monthly' | 'quarterly' | 'weekly';
type ViewMode = 'revenue' | 'units';
type GroupBy = 'customer' | 'sku';
type VarianceMode = 'percent' | 'absolute';

const ForecastAccuracy: React.FC = () => {
    const [forecasts, setForecasts] = useState<Forecast[]>([]);
    const [aggregationPeriod, setAggregationPeriod] = useState<AggregationPeriod>("monthly");
    const [selectedForecastId, setSelectedForecastId] = useState<number | null>(null);
    const [selectedForecastPeriodId, setSelectedForecastPeriodId] = useState<number | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>("revenue");
    const [groupBy, setGroupBy] = useState<GroupBy>("customer");
    const [varianceMode, setVarianceMode] = useState<VarianceMode>("percent");
    const [accuracyData, setAccuracyData] = useState<ForecastAccuracyData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loadingCsv, setLoadingCsv] = useState(false);
    const [loadingForecasts, setLoadingForecasts] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);

    const getToken = () => localStorage.getItem("token");

    useEffect(() => {
        const storedForecast = JSON.parse(localStorage.getItem('selectedForecast') || '{}');
        const storedForecastPeriodId = localStorage.getItem('selectedForecastPeriodId');

        if (storedForecast?.forecastId) {
            setSelectedForecastId(storedForecast.forecastId);
        }

        if (storedForecastPeriodId && storedForecastPeriodId !== "null") {
            setSelectedForecastPeriodId(parseInt(storedForecastPeriodId, 10));
        }

        fetchForecastsAndPeriods();
    }, []);

    const fetchForecastsAndPeriods = async () => {
        try {
            setLoadingForecasts(true);
            const token = getToken();
            const response = await axiosInstance.get<Forecast[]>("/forecasts", {
                headers: { Authorization: `Bearer ${token}` },
            });
            setForecasts(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingForecasts(false);
        }
    };

    const fetchAccuracyData = useCallback(async () => {
        if (!selectedForecastId || selectedForecastPeriodId === null) {
            setLoadingTable(false);
            return;
        }

        setLoadingTable(true);

        try {
            const params = {
                forecastPeriodId: selectedForecastPeriodId,
                aggregationPeriod,
                groupBy,
                viewMode
            };

            const response = await axiosInstance.get<ForecastAccuracyData>(`/forecasts/${selectedForecastId}/forecast-accuracy`, { params });
            setAccuracyData(response.data);
            setError(null);
        } catch (error: any) {
            console.error("Failed to fetch forecast accuracy:", error);
            setAccuracyData(null);
            setError(error.response?.status === 404 ? "Forecast accuracy data not found." : "An unexpected error occurred.");
        } finally {
            setLoadingTable(false);
        }
    }, [
        selectedForecastId,
        selectedForecastPeriodId,
        aggregationPeriod,
        groupBy,
        viewMode
    ]);

    useEffect(() => {
        fetchAccuracyData();
    }, [fetchAccuracyData]);

    const handleForecastSelected = (id: number) => {
        localStorage.setItem('selectedForecast', JSON.stringify({ forecastId: id }));
        setSelectedForecastId(id);
    };

    const handleForecastPeriodSelected = (forecastPeriodId: number) => {
        localStorage.setItem("selectedForecastPeriodId", forecastPeriodId.toString());
        setSelectedForecastPeriodId(forecastPeriodId);
    };

    const handleAggregationPeriodChange = (_event: React.SyntheticEvent, newValue: AggregationPeriod | null) => {
        if (newValue !== null) {
            setAggregationPeriod(newValue);
        }
    };

    const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newValue: ViewMode | null) => {
        if (newValue !== null) {
            setViewMode(newValue);
        }
    };

    const handleGroupByChange = (_event: React.MouseEvent<HTMLElement>, newValue: GroupBy | null) => {
        if (newValue !== null) {
            setGroupBy(newValue);
        }
    };

    const handleVarianceModeChange = (_event: React.MouseEvent<HTMLElement>, newValue: VarianceMode | null) => {
        if (newValue !== null) {
            setVarianceMode(newValue);
        }
    };

    const handleDownloadCsv = async () => {
        if (!selectedForecastPeriodId) return;
        
        try {
            const params = {
                forecastPeriodId: selectedForecastPeriodId,
                aggregationPeriod,
                groupBy,
                viewMode
            };
            
            const fileName = `ForecastAccuracy_${selectedForecastId}_${selectedForecastPeriodId}.csv`;

            await downloadCsv(`/forecasts/${selectedForecastId}/forecast-accuracy-csv`, params, fileName, setLoadingCsv, setError);
        } catch (error) {
            console.error("Unexpected error during CSV download:", error);
        }
    };

    const selectedForecast = forecasts.find((f) => f.forecastId === selectedForecastId);

    return (
        <Box 
            className="component-container"
            sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%',
                overflow: 'hidden'
            }}
        >
            <Paper elevation={2} sx={{ p: 3, mb: 2, flexShrink: 0 }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastDropdown
                                forecasts={forecasts}
                                selectedForecastId={selectedForecastId}
                                onForecastSelected={handleForecastSelected}
                                width={200}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastPeriodDropdown
                                forecastPeriods={selectedForecast?.forecastPeriods || []}
                                selectedForecastPeriodId={selectedForecastPeriodId}
                                onForecastPeriodSelected={handleForecastPeriodSelected}
                                width={200}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={handleViewModeChange}
                                aria-label="view mode"
                                size="medium"
                            >
                                <ToggleButton value="revenue" aria-label="revenue">
                                    Revenue
                                </ToggleButton>
                                <ToggleButton value="units" aria-label="units">
                                    Units
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ToggleButtonGroup
                                value={groupBy}
                                exclusive
                                onChange={handleGroupByChange}
                                aria-label="group by"
                                size="medium"
                            >
                                <ToggleButton value="customer" aria-label="customer">
                                    Customer
                                </ToggleButton>
                                <ToggleButton value="sku" aria-label="sku">
                                    SKU
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ToggleButtonGroup
                                value={varianceMode}
                                exclusive
                                onChange={handleVarianceModeChange}
                                aria-label="variance mode"
                                size="medium"
                            >
                                <ToggleButton value="percent" aria-label="percent variance">
                                    Percent
                                </ToggleButton>
                                <ToggleButton value="absolute" aria-label="absolute variance">
                                    Absolute
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="circular" width={32} height={32} />
                        ) : (
                            <Tooltip title="Download CSV">
                                <span>
                                    <IconButton onClick={handleDownloadCsv} aria-label="download csv" disabled={loadingCsv || !accuracyData}>
                                        {loadingCsv ? <CircularProgress size={24} /> : <FileDownloadIcon />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {error ? (
                <div>{error}</div>
            ) : (
                <>
                    <Paper elevation={1} sx={{ mb: 2, flexShrink: 0 }}>
                        <Tabs 
                            value={aggregationPeriod} 
                            onChange={handleAggregationPeriodChange}
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab label="Monthly" value="monthly" />
                            <Tab label="Quarterly" value="quarterly" />
                            <Tab label="Weekly" value="weekly" />
                        </Tabs>
                    </Paper>
                    {loadingTable ? (
                        <Skeleton variant="rectangular" width="100%" sx={{ flexGrow: 1 }} />
                    ) : (
                        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                            <ForecastAccuracyGrid 
                                data={accuracyData} 
                                selectedForecast={selectedForecast}
                                aggregationPeriod={aggregationPeriod}
                                viewMode={viewMode}
                                groupBy={groupBy}
                                varianceMode={varianceMode}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default ForecastAccuracy;

